// src/services/analytics.ts

import API_URL from "../config/api";

// =====================================================
// STORAGE KEYS
// =====================================================

const VISITOR_ID_KEY =
  "p_arena_visitor_id";

const SESSION_ID_KEY =
  "p_arena_analytics_session_id";


// =====================================================
// TYPES
// =====================================================

export type AnalyticsEventType =
  | "page_view"
  | "game_started"
  | "game_completed"
  | "login"
  | "registration";

export type FasoorGameMode =
  | "ai"
  | "friend"
  | "online";


// =====================================================
// CREATE RANDOM ID
// =====================================================

const createRandomId = (): string => {

  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return [
    Date.now().toString(36),
    Math.random()
      .toString(36)
      .slice(2),
    Math.random()
      .toString(36)
      .slice(2),
  ].join("-");
};


// =====================================================
// VISITOR ID
// =====================================================
//
// This identifies the browser over multiple visits.
//
// It is NOT the same as a login/account ID.
//
// =====================================================

export const getVisitorId =
  (): string => {

    let visitorId =
      localStorage.getItem(
        VISITOR_ID_KEY
      );

    if (visitorId) {
      return visitorId;
    }

    visitorId =
      createRandomId();

    localStorage.setItem(
      VISITOR_ID_KEY,
      visitorId
    );

    return visitorId;
  };


// =====================================================
// SESSION ID
// =====================================================

export const getAnalyticsSessionId =
  (): string | null => {

    return sessionStorage.getItem(
      SESSION_ID_KEY
    );
  };


const setAnalyticsSessionId = (
  sessionId: string
) => {

  sessionStorage.setItem(
    SESSION_ID_KEY,
    sessionId
  );
};


// =====================================================
// AUTH HEADERS
// =====================================================

const getHeaders = () => {

  const headers:
    Record<string, string> = {
      "Content-Type":
        "application/json",
    };

  const token =
    localStorage.getItem(
      "token"
    );

  if (token) {
    headers.Authorization =
      `Bearer ${token}`;
  }

  return headers;
};


// =====================================================
// DEVICE TYPE
// =====================================================

const getDeviceType =
  (): string => {

    const width =
      window.innerWidth;

    if (width <= 767) {
      return "mobile";
    }

    if (width <= 1024) {
      return "tablet";
    }

    return "desktop";
  };


// =====================================================
// BROWSER
// =====================================================

const getBrowser =
  (): string => {

    const agent =
      navigator.userAgent;

    if (
      agent.includes("Edg/")
    ) {
      return "Edge";
    }

    if (
      agent.includes("Firefox/")
    ) {
      return "Firefox";
    }

    if (
      agent.includes("Chrome/")
    ) {
      return "Chrome";
    }

    if (
      agent.includes("Safari/")
    ) {
      return "Safari";
    }

    return "Other";
  };


// =====================================================
// START VISITOR SESSION
// =====================================================
//
// sessionStorage means:
//
// - reload / navigation in the same tab keeps the session
// - a new browser tab gets its own session
// - closing the tab ends the browser-side session
//
// visitor_id remains in localStorage.
//
// =====================================================

let analyticsSessionPromise:
  Promise<string | null> | null =
    null;


// =====================================================
// START VISITOR SESSION
// =====================================================
//
// Only ONE session creation request may be in flight.
//
// This prevents startup effects such as:
//
//   ensureAnalyticsSession()
//   trackAnalyticsEvent("page_view")
//
// from creating two visitor_sessions rows at the
// same time.
//
// =====================================================

export const ensureAnalyticsSession =
  async (): Promise<
    string | null
  > => {

    // -------------------------------------------------
    // EXISTING SESSION
    // -------------------------------------------------

    const existingSessionId =
      getAnalyticsSessionId();

    if (existingSessionId) {
      return existingSessionId;
    }


    // -------------------------------------------------
    // SESSION CREATION ALREADY IN PROGRESS
    // -------------------------------------------------

    if (analyticsSessionPromise) {
      return analyticsSessionPromise;
    }


    // -------------------------------------------------
    // CREATE ONE SHARED SESSION REQUEST
    // -------------------------------------------------

    analyticsSessionPromise =
      (async () => {

        try {

          /*
            Check again because another call may have
            completed between the first check and the
            creation of this shared promise.
          */

          const currentSessionId =
            getAnalyticsSessionId();

          if (currentSessionId) {
            return currentSessionId;
          }


          const visitorId =
            getVisitorId();


          const response =
            await fetch(
              `${API_URL}/api/analytics/session`,
              {
                method: "POST",

                headers:
                  getHeaders(),

                body: JSON.stringify({
                  visitorId,

                  landingPage:
                    window.location.pathname,

                  deviceType:
                    getDeviceType(),

                  browser:
                    getBrowser(),
                }),
              }
            );


          if (!response.ok) {

            console.warn(
              "Analytics session request failed:",
              response.status
            );

            return null;
          }


          const data =
            await response.json();


          const sessionId =
            data?.session?.id;


          if (!sessionId) {

            console.warn(
              "Analytics session response did not contain a session ID."
            );

            return null;
          }


          const normalizedSessionId =
            String(sessionId);


          setAnalyticsSessionId(
            normalizedSessionId
          );


          return normalizedSessionId;

        } catch (error) {

          console.warn(
            "Analytics session unavailable:",
            error
          );

          return null;

        } finally {

          /*
            Allow a future retry if creation failed.

            If creation succeeded, future calls return
            the ID from sessionStorage before reaching
            this promise logic.
          */

          analyticsSessionPromise =
            null;
        }

      })();


    return analyticsSessionPromise;
  };


// =====================================================
// TRACK EVENT
// =====================================================

export const trackAnalyticsEvent =
  async (
    eventType:
      AnalyticsEventType,
    options?: {
      gameName?: string;
      gameMode?: FasoorGameMode;
      pagePath?: string;
    }
  ): Promise<void> => {

    try {

      const visitorId =
        getVisitorId();

      const sessionId =
        await ensureAnalyticsSession();

      if (!sessionId) {
        return;
      }

      await fetch(
        `${API_URL}/api/analytics/event`,
        {
          method: "POST",

          headers:
            getHeaders(),

          body: JSON.stringify({
            visitorId,
            sessionId,
            eventType,

            gameName:
              options?.gameName,

            gameMode:
              options?.gameMode,

            pagePath:
              options?.pagePath ??
              window.location.pathname,
          }),
        }
      );

    } catch (error) {

      /*
        Analytics must never prevent the user
        from using P-ARENA.
      */

      console.warn(
        "Analytics event unavailable:",
        error
      );
    }
  };
